import { useTranslation } from 'react-i18next';
import { Btn, Modal, Chip } from '@/presentation/component/ui';
import { inputClass } from '@/presentation/component/formStyles';
import { formatDate } from '@/presentation/util/format';
import { roleChipKind } from '@/presentation/util/roleChipKind';
import type { AdminUserProfile } from '@/domain/model/AdminUserProfile';
import type { Role } from '@/domain/model/Role';

const ROLES: Role[] = ['USER', 'ADMIN', 'SUPER_ADMIN'];

const toRole = (value: string): Role | null =>
  ROLES.includes(value as Role) ? (value as Role) : null;

interface ProfileDetailsGridProps {
  profile: AdminUserProfile;
  isSuperAdmin: boolean;
  onChangeRole: (id: string, role: Role) => void;
}

const ProfileDetailsGrid = ({ profile, isSuperAdmin, onChangeRole }: ProfileDetailsGridProps) => {
  const { t } = useTranslation();

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const role = toRole(e.target.value);
    if (role) onChangeRole(profile.id, role);
  };

  return (
    <div className="grid grid-cols-2 gap-4 rounded-lg border border-[var(--color-line)] p-4">
      <div>
        <p className="text-xs text-[var(--color-ink-faint)]">{t('admin.userRole')}</p>
        {isSuperAdmin ? (
          <select
            aria-label={t('admin.userRole')}
            value={profile.role}
            onChange={handleRoleChange}
            className={`${inputClass()} mt-1`}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`admin.role${r}`)}
              </option>
            ))}
          </select>
        ) : (
          <div className="mt-1">
            <Chip kind={roleChipKind(profile.role)}>{t(`admin.role${profile.role}`)}</Chip>
          </div>
        )}
      </div>
      <div>
        <p className="text-xs text-[var(--color-ink-faint)]">{t('admin.userPurchases')}</p>
        <p className="mt-1 font-medium text-[var(--color-ink)]">{profile.purchaseCount}</p>
      </div>
      <div>
        <p className="text-xs text-[var(--color-ink-faint)]">{t('admin.createdAt')}</p>
        <p className="mt-1 text-sm text-[var(--color-ink)]">{formatDate(profile.createdAt)}</p>
      </div>
      <div>
        <p className="text-xs text-[var(--color-ink-faint)]">{t('admin.updatedAt')}</p>
        <p className="mt-1 text-sm text-[var(--color-ink)]">{formatDate(profile.updatedAt)}</p>
      </div>
    </div>
  );
};

interface UserProfileModalProps {
  open: boolean;
  profile: AdminUserProfile | null;
  isLoading: boolean;
  isSuperAdmin: boolean;
  onChangeRole: (id: string, role: Role) => void;
  onClose: () => void;
}

export const UserProfileModal = ({
  open,
  profile,
  isLoading,
  isSuperAdmin,
  onChangeRole,
  onClose,
}: UserProfileModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal open={open} onClose={onClose} title={t('admin.userProfile')} size="md">
      {isLoading && (
        <p className="py-6 text-center text-[var(--color-ink-faint)]">{t('common.loading')}</p>
      )}
      {profile && !isLoading && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-accent-soft)] text-lg font-bold text-[var(--color-accent-ink)]">
              {profile.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-medium text-[var(--color-ink)]">{profile.name}</p>
              <p className="text-sm text-[var(--color-ink-soft)]">{profile.email}</p>
            </div>
          </div>
          <ProfileDetailsGrid
            profile={profile}
            isSuperAdmin={isSuperAdmin}
            onChangeRole={onChangeRole}
          />
          <div className="flex justify-end pt-2">
            <Btn kind="ghost" onClick={onClose}>
              {t('admin.cancel')}
            </Btn>
          </div>
        </div>
      )}
    </Modal>
  );
};
